import { ErrorBoundary } from 'react-error-boundary';
import { Route, Switch, usePath } from 'crossroad';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import AppSidebar from '@/components/app-sidebar';
import AppHeader from '@/components/app-header';
import { ForwardTo, requireUser, ShowError } from '@/lib/utils/utils';
import { AppDataHydrationProvider } from '@/lib/app-data-hydration';
import { AuthContext } from '@/lib/context';
import { use, useEffect } from 'react';
import OutlineView from '@/components/outline/outline-view';
import AdminPage from './AdminPage';
import OnboardingPage from './OnboardingPage';
import { PassageStateProvider } from '@/lib/context/passage-state-provider';
import { PassageProvider } from '@/lib/context/passage-provider';
import { ReadingViewWrapper } from '@/components/reading/reading-view-wrapper';
import AncientWorldMap from '@/components/ancient-world-map';
import FieldGuideOverview from '@/components/field-guide/field-guide-overview';
import FieldGuideArticle from '@/components/field-guide/field-guide-article';
import PassagePage from '@/components/passage/passage-page';
import { AncientWorldTimeline } from '@/components/ancient-world-timeline';
import { UserSettingsProvider } from '@/lib/context/user-settings-context';
import { FeatureGate } from '@/components/auth/feature-gate';
import { GuestBanner } from '@/components/auth/guest-banner';
import { SEO } from '@/components/seo';
import { FeedbackProvider, useFeedback } from '@/lib/context/feedback-context.tsx';
import { FeedbackModal } from '@/components/ui/feedback-modal.tsx';

import { ManuscriptsProvider } from '@/context/manuscripts-context';

export default function AppPage() {
    const auth = use(AuthContext);

    // Initialize anonymous user session on app entry
    useEffect(() => {
        if (auth) {
            import('@/lib/utils/utils').then(({ ensureUser }) => {
                ensureUser(auth).catch(err => console.error('Failed to initialize user session:', err));
            });
        }
    }, [auth]);

    return (
        <ErrorBoundary fallbackRender={({ error, resetErrorBoundary }) => <ShowError error={error as Error} resetErrorBoundary={resetErrorBoundary} />}>
            <AppDataHydrationProvider>
                <UserSettingsProvider>
                    <PassageStateProvider>
                        <ManuscriptsProvider>
                            <FeedbackProvider>
                                <SEO />
                                <PassageProvider>
                                    <FeedbackModalContainer />
                                    <GuestBanner />
                                    <SidebarProvider defaultOpen={false} style={{ "--sidebar-width": "calc(var(--spacing) * 72)", "--header-height": "calc(var(--spacing) * 12)" } as React.CSSProperties}>
                                        <AppSidebar variant="inset" />
                                        <SidebarInset>
                                            <AppHeader />
                                            <Switch>
                                                <Route path="/app" component={() => <ForwardTo path="/app/read" />} />
                                                <Route path="/app/onboarding" component={() => (
                                                    <FeatureGate featureName="Persona Onboarding" requireFullAccount>
                                                        <OnboardingPage />
                                                    </FeatureGate>
                                                )} />
                                                <Route path="/app/read/:book/:chapter" component={ReadingViewWrapper} />
                                                <Route path="/app/read" component={ReadingViewWrapper} />
                                                <Route path="/app/outline" component={OutlineView} />
                                                <Route path="/app/map" component={AncientWorldMap} />
                                                <Route path="/app/timeline" component={AncientWorldTimeline} />
                                                <Route path="/app/passage/:id*" component={PassagePage} />
                                                <Route path="/app/field-guide/:id" component={FieldGuideArticle} />
                                                <Route path="/app/field-guide" component={FieldGuideOverview} />
                                                <Route path="/app/manage*" component={AdminPage} />
                                            </Switch>
                                        </SidebarInset>
                                    </SidebarProvider>
                                </PassageProvider>
                            </FeedbackProvider>
                        </ManuscriptsProvider>
                    </PassageStateProvider>
                </UserSettingsProvider>
            </AppDataHydrationProvider>
        </ErrorBoundary>
    );
}

function FeedbackModalContainer() {
    const { isOpen, setIsOpen, feedbackContext } = useFeedback();
    return <FeedbackModal open={isOpen} onOpenChange={setIsOpen} context={feedbackContext} />;
}